import { apiFetch } from './client';

// Team mirrors the API shape served by /api/v1/organizations/{slug}/teams.
//
// `color` is the explicit override stored on members.team.color. Empty
// string means no override — the SPA uses lib/teamColor.derive(name) to
// generate a deterministic palette. `member_count` is populated by the
// list endpoint (correlated subquery against members.team_member); other
// endpoints leave it at zero.
export interface Team {
  id: string;
  name: string;
  description: string;
  color: string;
  // public_visibility — controls whether the team appears on the
  // public website (/teams page, team_rail block, /team/<slug>).
  // FALSE = admin-only. Toggled via the team edit modal.
  public_visibility: boolean;
  // category_id is the assigned members.team_category id, or "" when the
  // team is uncategorised. category_name is the joined display name.
  category_id: string;
  category_name: string;
  member_count: number;
  created_at: string;
  // Multilingual public description. description_source_language is the
  // language the description is written in; description_translations holds
  // per-language variants the public team page substitutes with source
  // fallback. Empty object when the team has no translations.
  description_source_language: string;
  description_translations: Record<string, string>;
}

export interface ListTeamsResponse {
  teams: Team[];
  total: number;
}

export interface ListTeamsOptions {
  search?: string;
  limit?: number;
  offset?: number;
  // Sort column id (mirrors the backend's allowlist in
  // internal/services/teams/service.go). Unknown values are silently
  // ignored server-side and fall back to the natural name ordering.
  sort?: 'name' | 'member_count' | 'created_at';
  order?: 'asc' | 'desc';
}

export async function listTeams(
  token: string | null,
  slug: string,
  opts: ListTeamsOptions = {},
): Promise<ListTeamsResponse> {
  const qs = new URLSearchParams();
  if (opts.search) qs.set('search', opts.search);
  if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
  if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
  if (opts.sort) qs.set('sort', opts.sort);
  if (opts.order) qs.set('order', opts.order);
  const query = qs.toString();
  const path = `/api/v1/organizations/${encodeURIComponent(slug)}/teams${query ? `?${query}` : ''}`;
  return apiFetch<ListTeamsResponse>(path, { token });
}

export async function getTeam(
  token: string | null,
  slug: string,
  teamID: string,
): Promise<Team> {
  return apiFetch<Team>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/teams/${encodeURIComponent(teamID)}`,
    { token },
  );
}

export interface CreateTeamInput {
  name: string;
  description?: string;
  // Optional 7-char hex (#aabbcc). Omit to use the deterministic auto-color.
  color?: string;
  public_visibility?: boolean;
  // Optional category to assign at creation. Empty string / omitted leaves
  // the team uncategorised. Edits to the category later use
  // assignTeamCategory.
  category_id?: string | undefined;
}

export async function createTeam(
  token: string | null,
  slug: string,
  input: CreateTeamInput,
): Promise<Team> {
  return apiFetch<Team>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/teams`,
    { method: 'POST', body: input, token },
  );
}

// Update is a full PATCH — the SPA always sends current values for all
// editable fields. Empty description / color clears any stored value;
// `name` is required (server returns 400 invalid_input on blank).
export interface UpdateTeamInput {
  name: string;
  description: string;
  color: string;
  public_visibility?: boolean;
  // Multilingual public description. Omit description_translations to leave
  // translations unchanged; send a map (even empty) to replace them.
  // description_source_language is the language the base description is in.
  description_source_language?: string;
  description_translations?: Record<string, string>;
}

export async function updateTeam(
  token: string | null,
  slug: string,
  teamID: string,
  input: UpdateTeamInput,
): Promise<Team> {
  return apiFetch<Team>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/teams/${encodeURIComponent(teamID)}`,
    { method: 'PATCH', body: input, token },
  );
}

// assignTeamCategory sets (or clears) a team's category. Pass categoryID
// = '' to clear; pass a UUID string to set. Uses a dedicated PUT endpoint
// so the tri-state (no-change vs clear vs set) doesn't need to be encoded
// in the main update payload.
export async function assignTeamCategory(
  token: string | null,
  slug: string,
  teamID: string,
  categoryID: string,
): Promise<Team> {
  return apiFetch<Team>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/teams/${encodeURIComponent(teamID)}/category`,
    { method: 'PUT', body: { category_id: categoryID === '' ? null : categoryID }, token },
  );
}

// Members of one team. Mirrors the GET /teams/{id}/members response. Role
// is one of the CHECK-constrained values on members.team_member.role.
export type TeamMemberRole = 'athlete' | 'leader';

export interface TeamMember {
  member_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: TeamMemberRole;
  // True when the member has a profile photo. Drives the avatar bubble
  // on the team-members table — render <img src=memberPhotoURL(...)>
  // when true, initials fallback when false.
  has_photo: boolean;
}

export interface ListTeamMembersResponse {
  members: TeamMember[];
  total: number;
}

export interface ListTeamMembersOptions {
  limit?: number;
  offset?: number;
}

export async function listTeamMembers(
  token: string | null,
  slug: string,
  teamID: string,
  opts: ListTeamMembersOptions = {},
): Promise<ListTeamMembersResponse> {
  const qs = new URLSearchParams();
  if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
  if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
  const query = qs.toString();
  const path = `/api/v1/organizations/${encodeURIComponent(slug)}/teams/${encodeURIComponent(teamID)}/members${query ? `?${query}` : ''}`;
  return apiFetch<ListTeamMembersResponse>(path, { token });
}
